import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { platformAPI } from "../services/api";

const slugify = (name) =>
  name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const Field = ({ label, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    {children}
  </div>
);

const inputClass =
  "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent";

export default function ShopNew() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    slug: "",
    slugTouched: false,
    phone: "",
    email: "",
    address: "",
    currency: "TND",
    language: "fr",
    device_limit: 3,
    owner_name: "",
    owner_email: "",
    owner_phone: "",
    // "new": a new owner account; "existing": the owner of a shop already here
    owner_mode: "new",
    owner_of_shop_id: "",
    join_pack: true,
  });
  const [shops, setShops] = useState([]);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    platformAPI
      .listShops()
      .then(({ data }) => setShops(data.filter((shop) => !shop.deleted_at && shop.owner_id)))
      .catch(() => setShops([]));
  }, []);

  // The shop whose owner opens another, and the pack that would pay for the new one
  const ownerShop = shops.find((shop) => shop.id === form.owner_of_shop_id);
  const packShop = ownerShop?.billing_shop_id
    ? shops.find((shop) => shop.id === ownerShop.billing_shop_id)
    : ownerShop;
  const packRoom = packShop
    ? (packShop.plan_shop_limit || 1) - 1 - (packShop.pack_member_count || 0)
    : 0;

  const set = (key) => (e) => {
    const value = e.target.value;
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "name" && !prev.slugTouched) next.slug = slugify(value);
      if (key === "slug") next.slugTouched = true;
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);

    try {
      const existing = form.owner_mode === "existing";
      const { data } = await platformAPI.createShop({
        name: form.name,
        slug: form.slug,
        phone: form.phone || undefined,
        email: form.email || undefined,
        address: form.address || undefined,
        currency: form.currency,
        language: form.language,
        device_limit: Number(form.device_limit) || 3,
        ...(existing
          ? { owner_of_shop_id: form.owner_of_shop_id, join_pack: form.join_pack }
          : {
              owner_name: form.owner_name,
              owner_email: form.owner_email,
              owner_phone: form.owner_phone || undefined,
            }),
      });
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || "Failed to create shop");
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <div className="max-w-lg bg-white rounded-xl shadow p-6">
        <h1 className="text-xl font-bold text-gray-900 mb-4">
          "{result.shop.name}" is ready
        </h1>
        <p className="text-sm text-gray-600 mb-1">
          Menu link: <code>/s/{result.shop.slug}</code>
        </p>
        <p className="text-sm text-gray-600 mb-1">
          Owner sign-in: <code>{result.owner.email}</code>
        </p>
        {form.owner_mode === "existing" ? (
          <p className="text-sm text-gray-600 mb-4">
            They sign in with the account and till PIN they already use, and switch to this shop
            from the shop's name at the top of their dashboard.
            {result.shop.billing_shop_id ? " It is paid for by their pack: one subscription for both." : ""}
          </p>
        ) : (
          <>
            <p className="text-sm text-gray-600 mb-1">
              A confirmation email was sent to the owner (in {form.language.toUpperCase()}). You can send it again
              from the shop's page if they did not get it.
            </p>
            <p className="text-sm text-gray-600 mb-4">
              Password: the default password (ChangeMe123!). Tell them to change it and set their
              till PIN from the dashboard's Staff screen.
            </p>
          </>
        )}
        <div className="flex gap-3">
          <button
            onClick={() => navigate(`/shops/${result.shop.id}`)}
            className="bg-primary-600 hover:bg-primary-700 text-white font-semibold px-4 py-2 rounded-lg"
          >
            Open shop
          </button>
          <button
            onClick={() => navigate("/shops")}
            className="text-gray-600 hover:underline"
          >
            Back to shops
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-lg">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">New shop</h1>

      {error && <p className="text-red-600 mb-4">{error}</p>}

      <form onSubmit={submit} className="bg-white rounded-xl shadow p-6 space-y-4">
        <Field label="Shop name">
          <input className={inputClass} value={form.name} onChange={set("name")} required />
        </Field>
        <Field label="Menu link slug">
          <input className={inputClass} value={form.slug} onChange={set("slug")} required />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Phone">
            <input className={inputClass} value={form.phone} onChange={set("phone")} />
          </Field>
          <Field label="Currency">
            <input className={inputClass} value={form.currency} onChange={set("currency")} />
          </Field>
        </div>
        <Field label="Email">
          <input className={inputClass} value={form.email} onChange={set("email")} />
        </Field>
        <Field label="Address">
          <input className={inputClass} value={form.address} onChange={set("address")} />
        </Field>
        <Field label="Device limit">
          <input
            type="number"
            min="1"
            className={inputClass}
            value={form.device_limit}
            onChange={set("device_limit")}
          />
        </Field>

        <hr className="my-2" />
        <h2 className="font-semibold text-gray-800">Owner account</h2>

        <div className="flex gap-4 text-sm">
          {[
            ["new", "A new owner"],
            ["existing", "Owner of a shop already here"],
          ].map(([value, label]) => (
            <label key={value} className="flex items-center gap-2">
              <input
                type="radio"
                name="owner_mode"
                value={value}
                checked={form.owner_mode === value}
                onChange={set("owner_mode")}
              />
              {label}
            </label>
          ))}
        </div>

        {form.owner_mode === "existing" ? (
          <>
            <Field label="Their shop">
              <select
                className={inputClass}
                value={form.owner_of_shop_id}
                onChange={set("owner_of_shop_id")}
                required
              >
                <option value="">Pick a shop…</option>
                {shops.map((shop) => (
                  <option key={shop.id} value={shop.id}>
                    {shop.name} — {shop.owner_name} ({shop.owner_email})
                  </option>
                ))}
              </select>
            </Field>
            <label className="flex items-start gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                className="mt-1"
                checked={form.join_pack}
                onChange={(e) => setForm((prev) => ({ ...prev, join_pack: e.target.checked }))}
              />
              <span>
                Paid by the same subscription (a pack)
                {packShop && (
                  <span className="block text-xs text-gray-500">
                    {packShop.plan_name
                      ? `${packShop.name} is on ${packShop.plan_name}, which covers ${packShop.plan_shop_limit || 1} shop(s): ${
                          packRoom > 0 ? `room for ${packRoom} more.` : "no room left — assign a bigger pack plan first."
                        }`
                      : `${packShop.name} has no plan yet: assign it a pack plan first.`}{" "}
                    The new shop starts on the pack's own dates (one trial for all).
                  </span>
                )}
              </span>
            </label>
          </>
        ) : null}

        <Field label="Language (the owner's emails and the shop's default)">
          <select className={inputClass} value={form.language} onChange={set("language")}>
            <option value="fr">Français</option>
            <option value="ar">العربية</option>
            <option value="en">English</option>
          </select>
        </Field>
        {form.owner_mode === "new" && (
        <>
        <Field label="Owner name">
          <input className={inputClass} value={form.owner_name} onChange={set("owner_name")} required />
        </Field>
        <Field label="Owner email (used to sign in)">
          <input
            type="email"
            className={inputClass}
            value={form.owner_email}
            onChange={set("owner_email")}
            required
          />
        </Field>
        <Field label="Owner phone">
          <input
            type="tel"
            className={inputClass}
            value={form.owner_phone}
            onChange={set("owner_phone")}
            required
          />
        </Field>
        </>
        )}

        <button
          type="submit"
          disabled={busy}
          className="w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 rounded-lg disabled:opacity-50"
        >
          {busy ? "Creating…" : "Create shop"}
        </button>
      </form>
    </div>
  );
}
